/**
 * The render world: engine, scene, cameras, ocean and sky.
 *
 * This layer READS simulation state and never mutates it. It owns nothing about rules —
 * only how the world looks.
 */

import { Engine } from '@babylonjs/core/Engines/engine';
import { Scene } from '@babylonjs/core/scene';
import { FreeCamera } from '@babylonjs/core/Cameras/freeCamera';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color4 } from '@babylonjs/core/Maths/math.color';
import { SEARCHLIGHT_MOUNT, searchlightStrike, searchlightDecay } from './constants';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { DirectionalLight } from '@babylonjs/core/Lights/directionalLight';
import { SpotLight } from '@babylonjs/core/Lights/spotLight';
import { Light } from '@babylonjs/core/Lights/light';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';

import '@babylonjs/core/Materials/standardMaterial';
import { SceneInstrumentation } from '@babylonjs/core/Instrumentation/sceneInstrumentation';
import '@babylonjs/core/Rendering/depthRendererSceneComponent';

import { createOceanGrid, OCEAN_GRID_PRESETS, type OceanGridOptions } from './ocean/oceanGrid';
import { OceanMaterial } from './ocean/oceanMaterial';
import { SkyDome } from './sky/skyDome';
import { createDetailNormalTexture, createFoamTexture, createCloudTexture } from './proceduralTextures';
import { computeEnvironment, type Environment } from './environment';
import { buildRig, type RigBuildResult } from './rig/rigBuilder';
import { createHullMaterial } from './rig/rigMaterials';
import { buildVisibleRigDetails } from './rig/visibleRigDetails';
import { SkyEnvironment } from './skyEnvironment';
import { createPostChain, type PostChain } from './postProcess';
import { mistState } from './mistPlugin';
import { VesselViews } from './ships/vesselViews';
import { EffectsSystem } from './fx/effects';
import { rigDamageLightScale } from './rigDamageLighting';
import { RigDamageEffects } from './rig/rigDamageEffects';
import {
  rigImpactPose,
  rigImpactStrength,
  type RigImpactCause,
  type RigImpactState,
} from './rigImpactCamera';

import { DECK_HEIGHT, EYE_X, EYE_Z } from './constants';
import { buildWaveField, type WaveField } from '../sim/waves';
import { WEATHER_PRESETS, type WeatherState, cloneWeather } from '../sim/weather';

export { DECK_HEIGHT, EYE_Z };

export type QualityTier = 'ultra' | 'high' | 'medium' | 'low';

export interface RenderWorldOptions {
  canvas: HTMLCanvasElement;
  quality?: QualityTier;
  /** Cap on device pixel ratio. Mobile defaults lower — see the performance plan. */
  maxPixelRatio?: number;
}



export class RenderWorld {
  readonly engine: Engine;
  readonly scene: Scene;
  readonly camera: FreeCamera;

  private readonly oceanMesh: Mesh;
  private readonly oceanMaterial: OceanMaterial;
  private readonly sky: SkyDome;
  private readonly sun: DirectionalLight;
  private readonly ambient: HemisphericLight;
  /** The trainable searchlight, aimed by the player. Off until `setSearchlight` says otherwise. */
  private readonly searchlight: SpotLight;
  private searchOn = false;
  /** Simulation time the beam was last switched, driving the strike and decay envelopes. */
  private searchSwitchedAt = -1e9;
  /** Held so a dying beam keeps its heading instead of swinging to the origin. */
  private searchDir = new Vector3(0, 0, 1);
  private readonly rig: RigBuildResult;
  private readonly skyEnv: SkyEnvironment;
  private readonly post: PostChain;
  private readonly rigDamageEffects: RigDamageEffects;
  private rigArtificial = 1;
  private rigLightScale = 1;
  /** Active platform jolt plus the unshaken player position it must return to. */
  private rigImpact: RigImpactState | null = null;
  private readonly rigImpactBase = new Vector3();

  /**
   * Draw-call and frame instrumentation.
   *
   * Babylon's raw `engine._drawCalls` counter accumulates across frames unless something
   * resets it per frame, which made the reported figure climb into the tens of thousands
   * and rendered the plan's ≤150 draw budget unmeasurable. SceneInstrumentation is what
   * performs that per-frame reset.
   */
  readonly instrumentation: SceneInstrumentation;

  /** Vessel instances and their wakes. Driven entirely by simulation state. */
  readonly vessels: VesselViews;
  /** Particles, tracers and lights for weapons, impacts and dying ships. */
  readonly effects: EffectsSystem;

  private weather: WeatherState;
  private waveField: WaveField;
  private environment: Environment;
  private quality: QualityTier;

  /** Simulation time in seconds, driving waves and scrolling. */
  time = 0;

  /**
   * The clock value most recently handed to the ocean and sky shaders.
   *
   * Distinct from `time` on purpose: the two silently diverged once, with the clock being
   * set every frame while nothing pushed it to the GPU, and the sea froze without a
   * single test noticing. Anything that wants to know whether the water is actually
   * animating asks this.
   */
  lastPushedTime = -1;

  readonly stats: { vertexCount: number; triangleCount: number };
  private vesselTriangleBaseline = 0;

  constructor(opts: RenderWorldOptions) {
    const quality = opts.quality ?? 'high';
    this.quality = quality;

    this.engine = new Engine(opts.canvas, true, {
      preserveDrawingBuffer: false,
      stencil: true,
      antialias: quality === 'ultra' || quality === 'high',
      powerPreference: 'high-performance',
      // Failing loudly beats silently rendering nothing.
      failIfMajorPerformanceCaveat: false,
    });

    const maxRatio = opts.maxPixelRatio ?? (quality === 'low' || quality === 'medium' ? 1.5 : 2);
    this.engine.setHardwareScalingLevel(1 / Math.min(window.devicePixelRatio || 1, maxRatio));

    this.scene = new Scene(this.engine);
    this.scene.clearColor = new Color4(0.05, 0.06, 0.07, 1);
    // Nothing in this game needs picking against the sea; skip the per-frame work.
    this.scene.skipPointerMovePicking = true;
    this.scene.autoClearDepthAndStencil = true;


    // Display transform is applied once, for the whole frame, by the post pipeline
    // (see render/postProcess.ts). Shaders and materials all output linear HDR.

    this.camera = new FreeCamera('wideCamera', new Vector3(EYE_X, DECK_HEIGHT, EYE_Z), this.scene);
    /*
     * The near plane must clear the handrail. The eye stands 0.95 m behind it (Adam's
     * stance, EYE_Z vs the rail's 4.35), and clipping is measured along the VIEW axis —
     * swing the camera 50° and a 0.95 m rail sits at 0.95·cos(50°) ≈ 0.61 m. At the old
     * minZ = 1 the rail sliced open whenever the view turned, which read as it going
     * transparent. 0.55 keeps it whole across the wide frustum; the cost is ~1.8×
     * coarser depth at range (≈11 m quantisation at 10 km, under fog), checked visually
     * on scope-on-tanker and convoy-inbound before shipping.
     */
    this.camera.minZ = 0.55;
    // From ~105m the geometric horizon is ~36.6km; the ocean mesh reaches 40km and the
    // far plane must clear it comfortably.
    this.camera.maxZ = 80000;
    this.camera.fov = 1.0472; // 60 degrees vertical — the reference wide framing.
    this.camera.setTarget(new Vector3(0, DECK_HEIGHT - 21, 100));

    this.weather = cloneWeather(WEATHER_PRESETS.overcastDay);
    this.waveField = buildWaveField({
      seaState: this.weather.seaState,
      windDir: this.weather.windDir,
      seed: 'deepwater',
    });
    this.environment = computeEnvironment(this.weather);

    // Lights. The sea is shaded analytically, so these exist for the rig and vessels.
    this.ambient = new HemisphericLight('ambient', new Vector3(0, 1, 0), this.scene);
    this.sun = new DirectionalLight('sun', new Vector3(-0.4, -0.7, 0.35), this.scene);

    /*
     * The searchlight as a real scene light, so hulls light up and not just water.
     *
     * The ocean is shaded analytically and takes its own copy of this beam through the
     * material; this light exists for everything else — chiefly the vessels the player is
     * hunting, which is the whole point of the instrument.
     *
     * `renderPriority` is load-bearing. The rig already carries fourteen lights against a
     * six-light material budget, and Babylon prunes per mesh by its own ranking; without
     * an explicit priority the searchlight would be dropped from exactly the distant
     * vessels it is meant to reveal.
     */
    this.searchlight = new SpotLight(
      'searchlight',
      new Vector3(SEARCHLIGHT_MOUNT.x, SEARCHLIGHT_MOUNT.y, SEARCHLIGHT_MOUNT.z),
      new Vector3(0, 0, 1),
      0.22,
      1.0,
      this.scene,
    );
    /*
     * Weighted hard toward specular, so a hull flares when its plating happens to catch
     * the beam and stays dim otherwise.
     *
     * A balanced lamp lights a ship like a studio fill — every facing surface glows the
     * same flat amount, which reads as a vessel that has been switched on rather than one
     * caught in a beam. Steel at sea is glossy and mostly returns light at the angles that
     * mirror it, so the small diffuse term is the little that scatters and the large
     * specular term is the flash that actually tells the player they have found something.
     */
    this.searchlight.diffuse = new Color3(0.17, 0.19, 0.23);
    this.searchlight.specular = new Color3(1.35, 1.50, 1.75);
    this.searchlight.intensity = 0;
    this.searchlight.range = 1700;
    this.searchlight.renderPriority = 100;
    /*
     * Linear falloff, not the physical inverse-square default.
     *
     * This is why hulls did not light up. Babylon's default falloff divides by distance
     * squared, so at 900 m a lamp contributes about a millionth of its intensity — the
     * beam was reaching the vessel and delivering nothing. That default is right for a
     * bare bulb radiating into a sphere and wrong for a searchlight, whose entire purpose
     * is to collimate a beam so it does NOT spread with distance. Linear falloff is the
     * closest of Babylon's models to a real one, and it is what makes a hull a kilometre
     * out actually flare when the beam crosses it.
     */
    this.searchlight.falloffType = Light.FALLOFF_STANDARD;
    this.searchlight.setEnabled(false);

    // High/Ultra are the reference-quality desktop tiers. Their ocean fields need
    // enough texels to survive 8x magnification; the smaller tiers retain the old
    // memory footprint for phones and integrated GPUs.
    const detailTextureSize = quality === 'ultra' || quality === 'high' ? 512 : 256;
    const foamTextureSize = quality === 'low' ? 256 : 512;
    const detailNormal = createDetailNormalTexture(this.scene, detailTextureSize);
    const foamTex = createFoamTexture(this.scene, foamTextureSize);
    const cloudTex = createCloudTexture(this.scene, 512);

    const gridOpts: OceanGridOptions = OCEAN_GRID_PRESETS[quality];
    const grid = createOceanGrid(this.scene, gridOpts);
    this.oceanMesh = grid.mesh;
    this.stats = { vertexCount: grid.vertexCount, triangleCount: grid.triangleCount };

    this.oceanMaterial = new OceanMaterial(this.scene, detailNormal, foamTex);
    this.oceanMesh.material = this.oceanMaterial.material;

    this.sky = new SkyDome(this.scene, cloudTex);

    this.skyEnv = new SkyEnvironment(this.scene);

    const mobileGeometry = quality === 'low' || quality === 'medium';
    this.rig = buildRig(this.scene, mobileGeometry ? 'mobile' : 'desktop');
    // The close inhabited-deck props are below a phone's useful pixel footprint and are
    // a poor trade against the Kraken's readable anatomy. Desktop keeps every detail.
    if (!mobileGeometry) {
      const visibleRigDetails = buildVisibleRigDetails(this.scene);
      this.rig.meshes.push(...visibleRigDetails.meshes);
      this.rig.drawCalls += visibleRigDetails.meshes.length;
    }
    this.oceanMaterial.setRigFloods(this.rig.lights.oceanFloods);
    this.stats.triangleCount += this.rig.meshes.reduce(
      (n, m) => n + (m.getTotalIndices() / 3), 0,
    );

    this.instrumentation = new SceneInstrumentation(this.scene);
    this.instrumentation.captureFrameTime = true;
    this.instrumentation.captureRenderTime = true;

    this.vessels = new VesselViews(this.scene, createHullMaterial(this.scene), quality);
    this.effects = new EffectsSystem(this.scene);
    this.rigDamageEffects = new RigDamageEffects(this.scene);
    this.stats.triangleCount += this.rigDamageEffects.triangleCount;
    this.vesselTriangleBaseline = this.vessels.triangleCount;
    this.stats.triangleCount += this.vesselTriangleBaseline;

    this.post = createPostChain(
      this.scene, this.camera, this.sun, this.rig.meshes, quality,
    );

    this.applyEnvironment();
  }

  get currentWeather(): WeatherState {
    return this.weather;
  }

  get currentWaveField(): WaveField {
    return this.waveField;
  }

  get currentEnvironment(): Environment {
    return this.environment;
  }

  /** The rig's accumulated meshes, for line-of-sight queries. */
  get rigMeshes(): readonly Mesh[] {
    return this.rig.meshes;
  }

  get qualityTier(): QualityTier {
    return this.quality;
  }

  /** Current formal triangle estimate, including whichever Kraken LOD is actually active. */
  get currentTriangleCount(): number {
    return this.stats.triangleCount - this.vesselTriangleBaseline + this.vessels.triangleCount;
  }

  setWeather(w: WeatherState, rebuildWaves = true): void {
    this.weather = cloneWeather(w);
    if (rebuildWaves) {
      this.waveField = buildWaveField({
        seaState: w.seaState,
        windDir: w.windDir,
        seed: 'deepwater',
      });
    }
    this.environment = computeEnvironment(this.weather);
    this.applyEnvironment();
  }

  private applyEnvironment(): void {
    const env = this.environment;
    this.skyEnv.update(env);
    this.oceanMaterial.setWaveField(this.waveField);
    this.oceanMaterial.setEnvironment(env, this.weather);
    this.sky.setEnvironment(env, this.weather);

    this.ambient.diffuse = env.ambientColor;
    this.ambient.groundColor = env.deepColor;
    this.ambient.intensity = env.ambientIntensity;

    this.sun.direction = new Vector3(-env.sunDir.x, -env.sunDir.y, -env.sunDir.z);
    this.sun.diffuse = env.sunColor;
    this.sun.intensity = env.directIntensity;

    this.scene.clearColor = new Color4(env.fogColor.r, env.fogColor.g, env.fogColor.b, 1);

    // One block of air for every material carrying the mist plugin (rig now, vessels
    // later), matching the terms the ocean shader integrates itself.
    mistState.air = env.mistAirDensity;
    mistState.sea = env.mistSeaDensity;
    mistState.heightScale = env.mistHeightScale;
    mistState.far = env.fogDensity;
    mistState.near = env.mistNearDensity;
    mistState.nearRange = env.mistNearRange;
    mistState.colorR = env.fogColor.r;
    mistState.colorG = env.fogColor.g;
    mistState.colorB = env.fogColor.b;



    // `post` is created after the rig, so guard the first call during construction.
    this.post?.setEnvironment(env);

    // Rig lighting is inverse to daylight: floodlights come up as the sun goes down.
    this.rigArtificial = 0.24 + 0.76 * env.rigLightStrength;
    /*
     * Floodlight power.
     *
     * Raised on Adam's direction — the rig read as under-lit at night, which undercuts the
     * one thing the platform has going for it in the dark: being the only lit object for
     * thirty kilometres. The cone and the fill are lifted together and by the same
     * proportion, because raising only the spot gives hard pools with black between them
     * and raising only the fill flattens the structure into a grey wash.
     */
    this.applyRigLightPower();

    // Wake foam and spray belong to the same palette as the sea's own whitecaps.
    this.vessels?.setFoamColor(env.foamColor);
    this.effects?.setEnvironment(env.foamColor);
  }

  /**
   * Make damage visible in the rig's electrical system.
   *
   * Every fixture and its reflection on the sea share one voltage envelope, so a brief
   * brownout reads as failed power rather than a lighting bug. The envelope comes only
   * from integrity and simulation time.
   */
  setRigIntegrity(integrity: number, time = this.time): void {
    this.rigLightScale = rigDamageLightScale(integrity, time);
    this.applyRigLightPower();
    this.rigDamageEffects.update(
      integrity,
      time,
      this.weather.windDir,
      this.weather.windSpeed,
      this.environment.fogColor,
      this.camera.position.x,
      this.camera.position.z,
    );
  }

  private applyRigLightPower(): void {
    const power = this.rigArtificial * this.rigLightScale;
    for (const spot of this.rig.lights.spots) {
      // The strong class exists so a handful of fixtures can dominate without lifting
      // every lamp on the rig — raising the floor flattens the structure, raising a few
      // gives it somewhere to be bright.
      spot.intensity = power * (
        // Deck floods light the sea from 100 m up, so they need an order of magnitude
        // more than a fixture washing a wall three metres away.
        spot.name.startsWith('floodDeck') ? 19000
          : spot.name.startsWith('floodStrong') ? 1040
            : 380);
    }
    for (const pl of this.rig.lights.points) {
      pl.intensity = power * (pl.name.startsWith('floodFillDeck') ? 11000
        : pl.name.startsWith('floodFillStrong') ? 600
        : pl.name.startsWith('floodFill') ? 220
          // Aviation warning lamps: named explicitly rather than left in a catch-all, so
          // they are obviously covered by a power change rather than covered by accident.
          : pl.name.startsWith('warnPoint') ? 28 : 28);
    }
    for (const lens of this.rig.lights.lampMeshes) {
      lens.visibility = 0.20 + 0.80 * this.rigLightScale;
    }
    this.oceanMaterial.setRigFloodPower(this.rigLightScale);
  }

  /**
   * Push per-frame uniforms at the current `time`, without advancing it.
   *
   * Separate from `update` because the simulation is the clock authority during play:
   * the sync layer assigns `world.time = sim.time` and then calls this. Folding the two
   * together is how the ocean silently froze — the clock was being set every frame while
   * nothing ever handed it to the shader, so the sea rendered at whatever time it had
   * been built with.
   */
  pushFrame(): void {
    this.lastPushedTime = this.time;
    const camPos = this.camera.position;
    this.oceanMaterial.setFrame(
      this.time,
      camPos,
      this.camera.fov,
      this.engine.getRenderHeight(),
    );
    this.sky.setFrame(this.time, camPos);
  }

  /**
   * Start the restrained platform movement for a hit, without changing the aim ray.
   *
   * The source is reduced to left/right in the player's current view. A second hit
   * refreshes the response but keeps the original unshaken camera position, so a salvo
   * cannot make the player slowly walk away from the controls.
   */
  triggerRigImpact(
    sourceX: number,
    sourceZ: number,
    damage: number,
    cause: RigImpactCause,
    time = this.time,
  ): void {
    if (!this.rigImpact) this.rigImpactBase.copyFrom(this.camera.position);

    const dx = sourceX - this.rigImpactBase.x;
    const dz = sourceZ - this.rigImpactBase.z;
    const distance = Math.max(0.001, Math.hypot(dx, dz));
    const yaw = this.camera.rotation.y;
    const rightX = Math.cos(yaw);
    const rightZ = -Math.sin(yaw);
    let side = (dx * rightX + dz * rightZ) / distance;
    // A dead-ahead source still needs a tiny readable lean. Base its direction only on
    // world position so replaying the same event produces the same frame.
    if (Math.abs(side) < 0.12) side = sourceX + sourceZ >= 0 ? 0.12 : -0.12;

    this.rigImpact = {
      startedAt: time,
      strength: rigImpactStrength(damage, cause),
      side,
      cause,
    };
    this.updateRigImpactCamera(time);
  }

  /** Reconstruct the current jolt from simulation time, rather than integrating frames. */
  updateRigImpactCamera(time = this.time): void {
    if (!this.rigImpact) return;
    const pose = rigImpactPose(time, this.rigImpact);
    if (!pose.active) {
      this.clearRigImpactCamera();
      return;
    }

    const yaw = this.camera.rotation.y;
    const rightX = Math.cos(yaw);
    const rightZ = -Math.sin(yaw);
    const forwardX = Math.sin(yaw);
    const forwardZ = Math.cos(yaw);
    this.camera.position.set(
      this.rigImpactBase.x + rightX * pose.right + forwardX * pose.forward,
      this.rigImpactBase.y + pose.up,
      this.rigImpactBase.z + rightZ * pose.right + forwardZ * pose.forward,
    );
    this.camera.rotation.z = pose.roll;
  }

  /** Restore the exact player camera after a hit or when a watch is torn down. */
  clearRigImpactCamera(): void {
    if (this.rigImpact) this.camera.position.copyFrom(this.rigImpactBase);
    this.camera.rotation.z = 0;
    this.rigImpact = null;
  }

  /**
   * Aim the searchlight along a compass bearing and depression, or pass null to douse it.
   *
   * Bearing is radians clockwise from +Z, matching the simulation's convention, so the
   * cone that lights the water is the same cone `updateDetection` credits for spotting.
   */
  setSearchlight(bearingRad: number | null, pitchRad = -0.12): void {
    const on = bearingRad !== null;
    if (on !== this.searchOn) {
      this.searchOn = on;
      this.searchSwitchedAt = this.time;
    }
    // Clocked off simulation time, not wall time, so a benchmark's frozen clock holds the
    // beam still instead of running the strike transient during a capture.
    const since = Math.max(0, this.time - this.searchSwitchedAt);
    const level = on ? searchlightStrike(since) : searchlightDecay(since);

    if (on) {
      const cosPitch = Math.cos(pitchRad);
      this.searchDir = new Vector3(
        Math.sin(bearingRad) * cosPitch,
        Math.sin(pitchRad),
        Math.cos(bearingRad) * cosPitch,
      ).normalize();
    }

    if (level <= 0.0008) {
      this.searchlight.setEnabled(false);
      this.searchlight.intensity = 0;
      this.oceanMaterial.setSearchlight(null, this.searchlight.position, this.searchDir);
      return;
    }

    // The beam keeps its last heading while it dies, so dousing it does not swing the
    // fading pool back to the world origin.
    this.searchlight.setEnabled(true);
    this.searchlight.direction = this.searchDir;
    // A searchlight competing with daylight is pointless; its authority arrives with
    // darkness, on the same envelope the rig's own floods use.
    const night = this.environment.rigLightStrength;
    // With linear falloff the intensity is a plain multiplier, so this is a small number
    // rather than the hundreds a physically-attenuated lamp would need.
    this.searchlight.intensity = 7.5 * (0.18 + 0.82 * night) * level;
    this.oceanMaterial.setSearchlight(
      (0.68 + 1.62 * night) * level, this.searchlight.position, this.searchDir,
    );
  }

  /** Advance visual time and push per-frame uniforms. */
  update(dt: number): void {
    this.time += dt;
    this.pushFrame();
  }

  render(): void {
    this.scene.render();
  }

  resize(): void {
    this.engine.resize();
  }

  dispose(): void {
    this.clearRigImpactCamera();
    this.instrumentation.dispose();
    this.vessels.dispose();
    this.effects.dispose();
    this.rigDamageEffects.dispose();
    this.post.dispose();
    this.skyEnv.dispose();
    this.oceanMaterial.dispose();
    this.sky.dispose();
    this.scene.dispose();
    this.engine.dispose();
  }
}
